/** @module @liquidefy/code-editor/layout/layout-gutter @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  ViewportState,
  GutterConfig,
  StickyScrollConfig,
  MarkerState,
  MarkerKind,
  LanguageId,
  FoldingRules,
  DocumentSymbol,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  clamp,
} from '../code-editor.js';

/**
 * A rendered line number entry for display.
 */
interface LineNumberEntry {
  readonly line: number;
  readonly display: string;
}

/**
 * A glyph positioned in the gutter margin.
 */
interface GutterGlyph {
  readonly line: number;
  readonly kind: MarkerKind;
  readonly icon: string;
  readonly priority: number;
}

/**
 * A gutter decoration derived from a marker.
 */
interface GutterDecoration {
  readonly line: number;
  readonly className: string;
  readonly hoverMessage?: string;
}

/**
 * A sticky header line to pin at the top of the viewport.
 */
export interface StickyHeader {
  readonly line: number;
  readonly content: string;
  readonly depth: number;
}

// ═══ §2 LINE NUMBERS — compute display numbers based on mode, width calculation ═══

/**
 * Computes line number entries for all visible lines in the viewport.
 */
export function computeLineNumbers(
  config: GutterConfig,
  totalLines: number,
  viewport: ViewportState,
): EditorResult<LineNumberEntry[]> {
  if (config.lineNumbers === 'off') {
    return ok([], 'LINE_NUMBERS_OFF', 'Line numbers disabled');
  }

  const entries: LineNumberEntry[] = [];
  const cursorLine = -1; // default: no cursor context for batch compute

  for (let line = viewport.startLine; line < viewport.endLine && line < totalLines; line++) {
    entries.push({
      line,
      display: formatLineNumber(line, config.lineNumbers, cursorLine),
    });
  }

  return ok(entries, 'LINE_NUMBERS', `Computed ${entries.length} line numbers`);
}

/**
 * Formats a single line number according to the display mode.
 */
export function formatLineNumber(
  line: number,
  mode: 'on' | 'off' | 'relative' | 'interval',
  cursorLine: number,
): string {
  // Lines are 0-indexed internally, display as 1-indexed
  const displayLine = line + 1;

  switch (mode) {
    case 'off':
      return '';
    case 'on':
      return String(displayLine);
    case 'relative': {
      if (line === cursorLine) return String(displayLine);
      const relative = Math.abs(line - cursorLine);
      return String(relative);
    }
    case 'interval': {
      // Show every 10th line number, and always the current line
      if (line === cursorLine) return String(displayLine);
      if (displayLine % 10 === 0) return String(displayLine);
      if (displayLine === 1) return String(displayLine);
      return '';
    }
    default:
      return String(displayLine);
  }
}

/**
 * Returns the character width needed to display the largest line number.
 */
export function lineNumberWidth(totalLines: number): number {
  return String(totalLines).length;
}

/**
 * Calculates the total gutter width in pixels.
 * Accounts for line number digits, folding controls, glyph margin, and decoration width.
 */
export function gutterTotalWidth(config: GutterConfig, totalLines: number): number {
  let width = 0;

  // Line numbers
  if (config.lineNumbers !== 'off') {
    const digitCount = lineNumberWidth(totalLines);
    const charPixels = 8; // approximate monospace character width
    width += digitCount * charPixels + 12; // padding
  }

  // Folding controls
  if (config.folding) {
    width += 16;
  }

  // Glyph margin
  if (config.glyphMargin) {
    width += 20;
  }

  // Line decorations
  width += config.lineDecorationsWidth;

  return width;
}

// ═══ §3 FOLDING — compute folding ranges, fold/unfold operations ═══

/**
 * Computes folding ranges from content using indentation and optional language rules.
 * Returns ranges where the start line has higher indentation than the next unindented line.
 */
export function computeFoldingRanges(
  content: string,
  _language: LanguageId,
  rules: FoldingRules | null,
): EditorResult<Range[]> {
  const lines = content.split('\n');
  const ranges: Range[] = [];

  // Region marker-based folding
  if (rules?.markers) {
    const startPattern = new RegExp(rules.markers.start);
    const endPattern = new RegExp(rules.markers.end);
    const startStack: number[] = [];

    for (let i = 0; i < lines.length; i++) {
      if (startPattern.test(lines[i]!)) {
        startStack.push(i);
      } else if (endPattern.test(lines[i]!) && startStack.length > 0) {
        const startLine = startStack.pop()!;
        ranges.push({
          start: { line: startLine, column: 0 },
          end: { line: i, column: lines[i]!.length },
        });
      }
    }
  }

  // Indentation-based folding
  const indentLevels = lines.map((line) => {
    if (line.trim().length === 0) return -1; // blank lines get -1
    let indent = 0;
    for (const ch of line) {
      if (ch === ' ') indent++;
      else if (ch === '\t') indent += 4;
      else break;
    }
    return indent;
  });

  for (let i = 0; i < lines.length; i++) {
    if (indentLevels[i]! < 0) continue;

    const currentIndent = indentLevels[i]!;
    let endLine = -1;

    // Find the last line that is more deeply indented (or blank)
    for (let j = i + 1; j < lines.length; j++) {
      if (indentLevels[j]! < 0) continue; // skip blank
      if (indentLevels[j]! > currentIndent) {
        endLine = j;
      } else {
        break;
      }
    }

    if (endLine > i) {
      ranges.push({
        start: { line: i, column: lines[i]!.length },
        end: { line: endLine, column: lines[endLine]!.length },
      });
    }
  }

  return ok(ranges, 'FOLDING_RANGES', `Found ${ranges.length} folding ranges`);
}

/**
 * Adds a fold to the set of folded ranges.
 */
export function foldRange(foldedRanges: Range[], range: Range): Range[] {
  // Avoid duplicates
  const exists = foldedRanges.some(
    (r) => r.start.line === range.start.line && r.end.line === range.end.line,
  );
  if (exists) return foldedRanges;
  return [...foldedRanges, range];
}

/**
 * Removes a fold from the set of folded ranges.
 */
export function unfoldRange(foldedRanges: Range[], range: Range): Range[] {
  return foldedRanges.filter(
    (r) => r.start.line !== range.start.line || r.end.line !== range.end.line,
  );
}

/**
 * Removes all folds.
 */
export function unfoldAll(_foldedRanges: Range[]): Range[] {
  return [];
}

/**
 * Folds all computed ranges in the content.
 */
export function foldAll(
  content: string,
  language: LanguageId,
  rules: FoldingRules | null,
): EditorResult<Range[]> {
  const result = computeFoldingRanges(content, language, rules);
  if (!result.success) return result;
  return ok(result.value, 'FOLD_ALL', `Folded ${result.value.length} ranges`);
}

/**
 * Folds ranges at a specific indentation level.
 */
export function foldLevel(
  content: string,
  language: LanguageId,
  rules: FoldingRules | null,
  level: number,
): EditorResult<Range[]> {
  const result = computeFoldingRanges(content, language, rules);
  if (!result.success) return result;

  const lines = content.split('\n');
  const filtered = result.value.filter((range) => {
    const line = lines[range.start.line] ?? '';
    let indent = 0;
    for (const ch of line) {
      if (ch === ' ') indent++;
      else if (ch === '\t') indent += 4;
      else break;
    }
    // Each indent "level" is tabSize (4) spaces
    return Math.floor(indent / 4) === level;
  });

  return ok(filtered, 'FOLD_LEVEL', `Folded ${filtered.length} ranges at level ${level}`);
}

/**
 * Checks whether a line is currently folded (hidden).
 */
export function isFolded(foldedRanges: Range[], line: number): boolean {
  return foldedRanges.some(
    (r) => line > r.start.line && line <= r.end.line,
  );
}

/**
 * Returns the total number of lines hidden by folds.
 */
export function foldedLineCount(foldedRanges: Range[]): number {
  let count = 0;
  for (const range of foldedRanges) {
    count += range.end.line - range.start.line;
  }
  return count;
}

/**
 * Returns the display label for a folded range: first line content + "..."
 */
export function foldedLineLabel(content: string, range: Range): string {
  const lines = content.split('\n');
  const firstLine = lines[range.start.line] ?? '';
  const hiddenCount = range.end.line - range.start.line;
  return `${firstLine.trimEnd()} ... (${hiddenCount} hidden lines)`;
}

// ═══ §4 GLYPH MARGIN — compute positioned markers, icons, sorting ═══

/**
 * Computes positioned glyph margin entries for visible markers.
 */
export function computeGlyphMargin(
  markers: MarkerState[],
  viewport: ViewportState,
): EditorResult<GutterGlyph[]> {
  const glyphs: GutterGlyph[] = [];

  for (const marker of markers) {
    const line = marker.range.start.line;
    if (line < viewport.startLine || line >= viewport.endLine) continue;

    glyphs.push({
      line,
      kind: marker.kind,
      icon: glyphForMarkerKind(marker.kind),
      priority: markerPriority(marker.kind),
    });
  }

  return ok(
    sortGlyphsByPriority(glyphs),
    'GLYPH_MARGIN',
    `Computed ${glyphs.length} glyph margin entries`,
  );
}

/**
 * Returns the icon/character representation for a marker kind.
 */
export function glyphForMarkerKind(kind: MarkerKind): string {
  switch (kind) {
    case 'bookmark': return '\u2605'; // star
    case 'breakpoint': return '\u25CF'; // filled circle
    case 'conditional-breakpoint': return '\u25D2'; // half circle
    case 'logpoint': return '\u25C6'; // diamond
    case 'annotation': return '\u270E'; // pencil
    case 'diagnostic': return '\u26A0'; // warning
    case 'fold-region': return '\u25BC'; // down triangle
    case 'changed-line': return '\u2502'; // vertical line
    case 'search-match': return '\u25B6'; // right triangle
    case 'linked-edit': return '\u21C4'; // arrows
    default: return '\u25CB'; // open circle
  }
}

/**
 * Converts a marker to a gutter decoration.
 */
export function markerToGutterDecoration(marker: MarkerState): GutterDecoration {
  const classMap: Record<MarkerKind, string> = {
    'bookmark': 'gutter-bookmark',
    'breakpoint': 'gutter-breakpoint',
    'conditional-breakpoint': 'gutter-conditional-breakpoint',
    'logpoint': 'gutter-logpoint',
    'annotation': 'gutter-annotation',
    'diagnostic': 'gutter-diagnostic',
    'fold-region': 'gutter-fold',
    'changed-line': 'gutter-changed',
    'search-match': 'gutter-search',
    'linked-edit': 'gutter-linked',
  };

  return {
    line: marker.range.start.line,
    className: classMap[marker.kind] ?? 'gutter-default',
    hoverMessage: 'data' in marker && typeof marker.data === 'object' && marker.data !== null
      ? (marker.data as Record<string, unknown>)['message'] as string | undefined
      : undefined,
  };
}

/**
 * Sorts glyphs by priority (highest priority first) so the most important
 * glyph renders on top when multiple markers occupy the same line.
 */
export function sortGlyphsByPriority(glyphs: GutterGlyph[]): GutterGlyph[] {
  return [...glyphs].sort((a, b) => b.priority - a.priority);
}

/**
 * Returns numeric priority for a marker kind.
 */
function markerPriority(kind: MarkerKind): number {
  const priorities: Record<MarkerKind, number> = {
    'breakpoint': 100,
    'conditional-breakpoint': 95,
    'logpoint': 90,
    'diagnostic': 80,
    'bookmark': 70,
    'annotation': 60,
    'search-match': 50,
    'changed-line': 40,
    'fold-region': 30,
    'linked-edit': 20,
  };
  return priorities[kind] ?? 0;
}

// ═══ §5 STICKY SCROLL — compute pinned scope headers at viewport top ═══

/**
 * Computes the sticky headers that should be pinned at the top of the viewport.
 * Based on document symbols (outline) and the current scroll position.
 */
export function computeStickyHeaders(
  content: string,
  symbols: DocumentSymbol[],
  viewport: ViewportState,
  config: StickyScrollConfig,
): EditorResult<StickyHeader[]> {
  if (!config.enabled) {
    return ok([], 'STICKY_DISABLED', 'Sticky scroll disabled');
  }

  const lines = content.split('\n');
  const headers: StickyHeader[] = [];
  const maxDepth = maxStickyDepth(config);
  const topLine = viewport.startLine;

  // Find all ancestor scopes of the top visible line
  const ancestors = findAncestorScopes(symbols, topLine, 0);

  // Limit to maxDepth and create headers
  const limited = ancestors.slice(0, maxDepth);

  for (const ancestor of limited) {
    const lineNum = ancestor.range.start.line;
    const lineContent = lines[lineNum] ?? '';
    headers.push({
      line: lineNum,
      content: lineContent,
      depth: ancestor.depth,
    });
  }

  return ok(headers, 'STICKY_HEADERS', `Computed ${headers.length} sticky headers`);
}

/**
 * Returns the scope depth of a line relative to document symbols.
 */
export function stickyHeaderDepth(symbols: DocumentSymbol[], line: number): number {
  const ancestors = findAncestorScopes(symbols, line, 0);
  return ancestors.length;
}

/**
 * Returns the maximum allowed sticky header depth from config.
 */
export function maxStickyDepth(config: StickyScrollConfig): number {
  return clamp(config.maxLineCount, 1, 10);
}

/**
 * Returns the content text of a sticky header line.
 */
export function stickyHeaderContent(content: string, line: number): string {
  const lines = content.split('\n');
  return lines[line] ?? '';
}

// ═══ INTERNAL HELPERS ═══

interface ScopeAncestor {
  readonly range: Range;
  readonly depth: number;
}

/**
 * Recursively finds all symbol scopes that contain the given line.
 */
function findAncestorScopes(
  symbols: DocumentSymbol[],
  line: number,
  depth: number,
): ScopeAncestor[] {
  const results: ScopeAncestor[] = [];

  for (const symbol of symbols) {
    if (line >= symbol.range.start.line && line <= symbol.range.end.line) {
      // This scope contains our line
      results.push({ range: symbol.range, depth });

      // Check children for deeper scopes
      if (symbol.children) {
        const childResults = findAncestorScopes(symbol.children, line, depth + 1);
        results.push(...childResults);
      }
    }
  }

  return results;
}
